import { i as isArray_1 } from './isArray-de6dfa07.js';
import { i as isSymbol_1 } from './isSymbol-4d636b7c.js';
import { _ as _stringToPath } from './stz-pagination-1c07fabf.js';
import { t as toString_1 } from './toString-22fd0fbf.js';

function* concat(source, ...others) {
  yield* source;
  for (const other of others) {
    yield* other;
  }
}
function* distinct(iterable) {
  const unique = new Set();
  for (const item of iterable) {
    if (!unique.has(item)) {
      unique.add(item);
      yield item;
    }
  }
}
function* filter$1(iterable, predicate) {
  for (const item of iterable) {
    if (predicate(item)) {
      yield item;
    }
  }
}
function* flatMap(iterable, mapperFn) {
  for (const item of iterable) {
    const sequence = mapperFn(item);
    yield* sequence;
  }
}
function* groupBy(iterable, keySelector, elementSelector) {
  const groups = new Map();
  for (const item of iterable) {
    let key = keySelector(item);
    // Check if we need to hash the key so that equality works correctly
    // with Map. This is to support object's (and array's) as the key.
    // We don't want to hash every single item, because it slows down
    // the algorithm a lot.
    const needToHash = typeof key === "object";
    if (needToHash) {
      key = JSON.stringify(key);
    }
    const value = elementSelector ? elementSelector(item) : item;
    let group = groups.get(key);
    if (!group) {
      group = {
        isKeyHashed: needToHash,
        items: []
      };
      groups.set(key, group);
    }
    group.items.push(value);
  }
  for (const keyItemsPair of groups.entries()) {
    yield {
      key: keyItemsPair[1].isKeyHashed ? JSON.parse(keyItemsPair[0]) : keyItemsPair[0],
      items: keyItemsPair[1].items
    };
  }
}
function* map(source, mapFn) {
  for (const item of source) {
    yield mapFn(item);
  }
}
function* prepend(source, ...others) {
  for (const other of others) {
    yield* other;
  }
  yield* source;
}

/**
 * Copies items from the given iterable into an array
 */
function copyIntoAnArray(iterable) {
  // Optimization for arrays, as .slice() by far fastest way to
  // create a shallow copy https://jsbench.me/pmk2aqrlo1
  if (Array.isArray(iterable)) {
    return iterable.slice();
  }
  // With iterables for-of loop is the fastest https://jsbench.me/pwk2ar61ei
  const result = [];
  for (const item of iterable) {
    result.push(item);
  }
  return result;
}
/**
 * Creates an iterable from given generator function
 *
 * @param generatorFn
 * @param args
 */
const iterableFromGenerator = (generatorFn, args) => ({
  [Symbol.iterator]: () => generatorFn.apply(undefined, args)
});
/**
 * Returns true if the given value is not null or undefined
 */
const isNotNullable = x => x !== null && x !== undefined;
function* reverse(iterable) {
  const items = copyIntoAnArray(iterable);
  for (let i = items.length - 1; i >= 0; i--) {
    yield items[i];
  }
}
function* skip(iterable, howMany) {
  let numSkipped = 0;
  for (const item of iterable) {
    if (numSkipped < howMany) {
      numSkipped++;
      continue;
    }
    yield item;
  }
}
function* skipWhile(iterable, predicate) {
  let startTaking = false;
  for (const item of iterable) {
    if (startTaking) {
      yield item;
    } else if (!predicate(item)) {
      yield item;
      startTaking = true;
    }
  }
}
function* sortBy(iterable, comparer) {
  yield* copyIntoAnArray(iterable).sort(comparer);
}
function* take(iterable, howMany) {
  if (howMany < 1) {
    return;
  }
  let numTaken = 0;
  for (const item of iterable) {
    numTaken++;
    yield item;
    if (numTaken >= howMany) {
      break;
    }
  }
}
function* takeWhile(iterable, predicate) {
  for (const item of iterable) {
    if (!predicate(item)) {
      break;
    }
    yield item;
  }
}
function* without(iterator, withoutItems, comparePredicate) {
  // cache already found results
  const cache = new Set();
  outer: for (const item of iterator) {
    // fast path, this item was already found, don't loop
    if (cache.has(item)) continue;
    // slow path, loop over each item in the set, determine if it matches the ComparePredicate
    for (const withoutItem of withoutItems) {
      // if the item is found, add it to the cache and skip the item
      if (comparePredicate(item, withoutItem)) {
        cache.add(item);
        continue outer;
      }
    }
    // we can safely yield the item
    yield item;
  }
}
const identityPredicateFn = x => x;
const defaultComparer = (a, b) => {
  if (a === b) {
    return 0;
  }
  if (a < b) {
    return -1;
  }
  return 1;
};
/**
 * A sequence of items
 */
class SequenceImpl {
  constructor(_iterable) {
    this._iterable = _iterable;
    this[Symbol.iterator] = () => this._iterable[Symbol.iterator]();
  }
  concat(...others) {
    return this._sequenceFromGenerator(concat, others);
  }
  distinct() {
    return this._sequenceFromGenerator(distinct);
  }
  every(predicate = identityPredicateFn) {
    for (const item of this._iterable) {
      if (!predicate(item)) {
        return false;
      }
    }
    return true;
  }
  filter(predicate) {
    return this._sequenceFromGenerator(filter$1, [predicate]);
  }
  find(predicate) {
    for (const item of this._iterable) {
      if (predicate(item)) {
        return item;
      }
    }
    return undefined;
  }
  first() {
    for (const item of this._iterable) {
      return item;
    }
    return undefined;
  }
  flatMap(mapperFn) {
    return this._sequenceFromGenerator(flatMap, [mapperFn]);
  }
  forEach(callback) {
    for (const item of this._iterable) {
      callback(item);
    }
  }
  groupBy(keySelector, elementSelector) {
    if (typeof keySelector === "string") {
      keySelector = createSelectByKey(keySelector);
    }
    return this._sequenceFromGenerator(groupBy, [keySelector, elementSelector]);
  }
  includes(searchItem) {
    for (const item of this._iterable) {
      if (item === searchItem) {
        return true;
      }
    }
    return false;
  }
  isEmpty() {
    return !this.some(() => true);
  }
  last() {
    const items = this.toArray();
    return items.length === 0 ? undefined : items[items.length - 1];
  }
  max() {
    let result = undefined;
    for (const item of this._iterable) {
      if (result === undefined) {
        result = item;
      } else if (item > result) {
        result = item;
      }
    }
    return result;
  }
  map(mapFn) {
    return this._sequenceFromGenerator(map, [mapFn]);
  }
  mapNotNullable(mapFn) {
    return this._composeGenerators(map, [mapFn], filter$1, [isNotNullable]);
  }
  min() {
    let result = undefined;
    for (const item of this._iterable) {
      if (result === undefined) {
        result = item;
      } else if (item < result) {
        result = item;
      }
    }
    return result;
  }
  pick(...keys) {
    return this.map(item => {
      const result = {};
      for (const key of keys) {
        if (key in item) {
          result[key] = item[key];
        }
      }
      return result;
    });
  }
  prepend(...items) {
    return this._sequenceFromGenerator(prepend, items);
  }
  reduce(callback, accumulator) {
    for (const item of this._iterable) {
      accumulator = callback(accumulator, item);
    }
    return accumulator;
  }
  reverse() {
    return this._sequenceFromGenerator(reverse);
  }
  skip(howMany) {
    return this._sequenceFromGenerator(skip, [howMany]);
  }
  skipWhile(predicate) {
    return this._sequenceFromGenerator(skipWhile, [predicate]);
  }
  some(predicate = identityPredicateFn) {
    for (const item of this._iterable) {
      if (predicate(item)) {
        return true;
      }
    }
    return false;
  }
  sortBy(keySelector, comparer) {
    const compareFn = createCompareFn(false, keySelector, comparer);
    return new OrderedSequenceImpl(this._iterable, compareFn);
  }
  sortByDescending(keySelector, comparer) {
    const compareFn = createCompareFn(true, keySelector, comparer);
    return new OrderedSequenceImpl(this._iterable, compareFn);
  }
  sum(valueSelector) {
    let result = undefined;
    for (const item of this._iterable) {
      let itemToSum = valueSelector ? valueSelector(item) : item;
      if (result === undefined) {
        result = itemToSum;
      } else {
        result += typeof itemToSum === "number" ? itemToSum : String(itemToSum);
      }
    }
    return result === undefined ? 0 : result;
  }
  take(howMany) {
    return this._sequenceFromGenerator(take, [howMany]);
  }
  takeWhile(predicate) {
    return this._sequenceFromGenerator(takeWhile, [predicate]);
  }
  without(items, predicate) {
    if (!predicate) {
      const withoutSet = new Set(items);
      return this.filter(item => !withoutSet.has(item));
    } else {
      return this._sequenceFromGenerator(without, [items, predicate]);
    }
  }
  toArray() {
    return copyIntoAnArray(this._iterable);
  }
  toMap(keySelectorFn, elementSelectorFn) {
    const map = new Map();
    for (const item of this._iterable) {
      const key = keySelectorFn(item);
      const value = elementSelectorFn ? elementSelectorFn(item) : item;
      map.set(key, value);
    }
    return map;
  }
  toObject(keySelectorFn, elementSelectorFn) {
    const object = {};
    for (const item of this._iterable) {
      const key = keySelectorFn(item);
      const value = elementSelectorFn ? elementSelectorFn(item) : item;
      object[key] = value;
    }
    return object;
  }
  toSet() {
    return new Set(this._iterable);
  }
  toString(separator = ",") {
    return Array.isArray(this._iterable) ? this._iterable.join(separator) : copyIntoAnArray(this._iterable).join(separator);
  }
  _sequenceFromGenerator(factoryFn, restArgs) {
    const iterableArg = [this._iterable];
    return new SequenceImpl(iterableFromGenerator(factoryFn, restArgs ? iterableArg.concat(restArgs) : iterableArg));
  }
  _composeGenerators(first, firstArgs, second, secondArgs) {
    const iterableArg = [this._iterable, ...firstArgs];
    const intemediatery = iterableFromGenerator(first, iterableArg);
    return new SequenceImpl(iterableFromGenerator(second, [intemediatery, ...secondArgs]));
  }
}
/**
 * Ordered sequence of elements
 */
class OrderedSequenceImpl extends SequenceImpl {
  constructor(_iterableToSort, comparer) {
    super(iterableFromGenerator(sortBy, [_iterableToSort, comparer]));
    this._iterableToSort = _iterableToSort;
    this._comparer = comparer;
  }
  thenBy(keySelector, comparer) {
    const thenComparer = createCompareFn(false, keySelector, comparer);
    const compareFn = createChainedCompareFn(this._comparer, thenComparer);
    return new OrderedSequenceImpl(this._iterableToSort, compareFn);
  }
  thenByDescending(keySelector, comparer) {
    const thenComparer = createCompareFn(true, keySelector, comparer);
    const compareFn = createChainedCompareFn(this._comparer, thenComparer);
    return new OrderedSequenceImpl(this._iterableToSort, compareFn);
  }
}
/**
 * Creates a compare function that sorts TItems either ascending or descending
 * using the given key selector and comparer.
 *
 * @param descending  Sort descending or ascending
 * @param keySelector Optional function to select the key that is used for sorting
 * @param comparer    Optional comparer function to compare the keys
 */
const createCompareFn = (descending, keySelector, comparer) => {
  const compareFn = comparer || defaultComparer;
  return keySelector ? (a, b) => descending ? compareFn(keySelector(b), keySelector(a)) : compareFn(keySelector(a), keySelector(b)) : (a, b) => descending ? defaultComparer(b, a) : defaultComparer(a, b);
};
/**
 * Chains two compare functions. First sort by firstCompare. If items
 * are equal, then sorts by secondCompare.
 *
 * @param firstCompare
 * @param secondCompare
 */
const createChainedCompareFn = (firstCompare, secondCompare) => (a, b) => {
  const firstResult = firstCompare(a, b);
  return firstResult === 0 ? secondCompare(a, b) : firstResult;
};
const createSelectByKey = key => item => key in item ? item[key] : undefined;
function createObjectIterable(object) {
  return iterableFromGenerator(objectIterator, [object]);
}
function* objectIterator(object) {
  for (const key in object) {
    if (object.hasOwnProperty(key)) {
      const value = object[key];
      yield [key, value];
    }
  }
}

/**
 * Entrypoint of the library
 */
function _isIterable(object) {
  return typeof object[Symbol.iterator] === "function";
}
function from(iterable) {
  if (iterable instanceof SequenceImpl) {
    return iterable;
  } else if (_isIterable(iterable)) {
    return new SequenceImpl(iterable);
  } else {
    return new SequenceImpl(createObjectIterable(iterable));
  }
}

function filterAlphabetic(filter, rowValue) {
  const filterValue = filter.value;
  const rowNull = rowValue == null;
  const filterNull = filterValue == null;
  const a = rowNull ? '' : String(rowValue);
  const b = filterNull ? '' : String(filterValue);
  let method = filter.method || 'contains';
  const inverse = method[0] === '!';
  let result;
  if (inverse) {
    method = method.substring(1);
  }
  // TODO: Add empty to text filter methods
  // if (method === 'empty') {
  //   result = rowNull || rowValue === '';
  //   return inverse ? result : !result;
  // }
  if (Array.isArray(filterValue)) {
    return filterValue[inverse ? 'every' : 'some'](v => filterAlphabetic(Object.assign(Object.assign({}, filter), { value: v }), rowValue));
  }
  // No filter value and mode not EQUALS should always pass
  if (filterNull || (filterValue === '' && method !== 'equals')) {
    return true;
    // Match null row values with empty string filter when mode is EQUALS
  }
  else if (rowNull && filterValue === '' && method === 'equals') {
    return !inverse;
    // filter out null row values
  }
  else if (rowNull) {
    return false;
  }
  switch (method) {
    case 'equals':
      result = a === b;
      break;
    case 'startsWith':
      result = a.toLowerCase().startsWith(b.toLowerCase());
      break;
    case 'endsWith':
      result = !!a.length && a.toLowerCase().endsWith(b.toLowerCase());
      break;
    case 'contains':
    default:
      result = a.toLowerCase().includes(b.toLowerCase());
      break;
  }
  return inverse ? !result : result;
}
function compareAlphabetic(direction, a, b) {
  // treat null && undefined as empty string
  a = a == null ? '' : String(a);
  b = b == null ? '' : String(b);
  // sort empty strings last (similar to Excel)
  if (a === '' && b === '') {
    return 0;
  }
  else if (a === '') {
    return 1;
  }
  else if (b === '') {
    return -1;
  }
  return a.localeCompare(b) * direction;
}
function ensureNumber(v) {
  const falsy = v === '' || typeof v === 'undefined' || v === null;
  return falsy ? NaN : Number(v);
}
function filterNumeric(filter, rowValue) {
  const filterData = filter.value;
  let method = filter.method || 'between';
  const inverse = method[0] === '!';
  // trim the '!' for inverse filters
  if (inverse) {
    method = method.substring(1);
  }
  const isBetween = method === 'between';
  const numValue = ensureNumber(rowValue);
  const filterValue = ensureNumber(filterData);
  const min = isBetween && ensureNumber(filterData === null || filterData === void 0 ? void 0 : filterData.a);
  const max = isBetween && ensureNumber(filterData === null || filterData === void 0 ? void 0 : filterData.b);
  const isRowValNaN = isNaN(numValue);
  const isFilterValNaN = isBetween ? (isNaN(min) && isNaN(max)) : isNaN(filterValue);
  // NaN filter value allow everything
  // TODO: add 'empty' method check
  if (isFilterValNaN /* && method !== 'empty' */) {
    return true;
  }
  // NaN row value always removed
  // TODO: add 'empty' method check
  if (isRowValNaN /* && method !== 'empty' */) {
    return false;
  }
  let result;
  switch (method) {
    case 'between':
      result = (isNaN(min) || numValue >= min) && (isNaN(max) || numValue <= max);
      break;
    case 'gt':
      result = (numValue > filterValue);
      break;
    case 'gte':
      result = (numValue >= filterValue);
      break;
    case 'lt':
      result = (numValue < filterValue);
      break;
    case 'lte':
      result = (numValue <= filterValue);
      break;
    // TODO: add 'empty' method check
    // case 'empty':
    //   result = rowValue == null || rowValue === '';
    //   break;
    case 'equals':
    default:
      result = (numValue === filterValue);
  }
  return inverse ? !result : result;
}
function compareNumeric(direction, a, b) {
  a = ensureNumber(a);
  b = ensureNumber(b);
  // sort invalid/empty values last (similar to Excel)
  if (isNaN(a) && isNaN(b)) {
    return 0;
  }
  else if (isNaN(a)) {
    return 1;
  }
  else if (isNaN(b)) {
    return -1;
  }
  return (a - b) * direction;
}
/**
 * Maps the DateFilter value into an object with `fromDate` and `toDate` props.
 * It will return null for either prop, if the method doesn't need it
 * E.g if method === 'before', fromDate is null, since we dont care about it
 *
 * This makes the comparison later on, easier to do
 */
function getDateFilterValues(filter) {
  const method = filter.method || 'between';
  const filterValue = filter.value;
  const result = { fromDate: null, toDate: null };
  switch (method) {
    case 'after':
      result.fromDate = filterValue;
      break;
    case 'before':
      result.toDate = filterValue;
      break;
    case 'equals':
    case '!equals':
      result.fromDate = filterValue;
      result.toDate = filterValue;
      break;
    case 'between':
    default:
      result.fromDate = filterValue === null || filterValue === void 0 ? void 0 : filterValue.a;
      result.toDate = filterValue === null || filterValue === void 0 ? void 0 : filterValue.b;
      break;
  }
  return result;
}
function filterDate(filter, rowValue) {
  const filterValue = getDateFilterValues(filter);
  const rowDate = new Date(rowValue == null ? NaN : rowValue);
  const fromDate = new Date(filterValue.fromDate || NaN);
  const toDate = new Date(filterValue.toDate || NaN);
  // shortcircut for empty filter value
  if (!isFinite(fromDate.valueOf()) && !isFinite(toDate.valueOf())) {
    return true;
  }
  // set fromDate to beginning of day to ignore time
  fromDate.setHours(0, 0, 0, 0);
  // set toDate to end of day to ignore time
  toDate.setHours(23, 59, 59, 999);
  // shortcircut for invalid/empty row value
  if ((isFinite(rowDate.valueOf()) === false)) {
    return false;
  }
  // ignore fromDate if invalid (e.g when method === 'before')
  const isAfterFromDate = !isFinite(fromDate.valueOf()) || rowDate >= fromDate;
  // ignore toDate if invalid (e.g when method === 'after')
  const isBeforeToDate = !isFinite(toDate.valueOf()) || rowDate <= toDate;
  // this will return the correct result for all cases, except !equals
  const result = isAfterFromDate && isBeforeToDate;
  // inverse result for !equals
  return filter.method === '!equals' ? !result : result;
}
// logic is same as comparing numbers so we just call compareNumeric
function compareDate(direction, a, b) {
  a = new Date(a);
  b = new Date(b);
  return compareNumeric(direction, a === null || a === void 0 ? void 0 : a.valueOf(), b === null || b === void 0 ? void 0 : b.valueOf());
}

/** Used to match property names within property paths. */
var reIsDeepProp = /\.|\[(?:[^[\]]*|(["'])(?:(?!\1)[^\\]|\\.)*?\1)\]/,
  reIsPlainProp = /^\w*$/;

/**
 * Checks if `value` is a property name and not a property path.
 *
 * @private
 * @param {*} value The value to check.
 * @param {Object} [object] The object to query keys on.
 * @returns {boolean} Returns `true` if `value` is a property name, else `false`.
 */
function isKey(value, object) {
  if (isArray_1(value)) {
    return false;
  }
  var type = typeof value;
  if (type == 'number' || type == 'symbol' || type == 'boolean' || value == null || isSymbol_1(value)) {
    return true;
  }
  return reIsPlainProp.test(value) || !reIsDeepProp.test(value) || object != null && value in Object(object);
}
var _isKey = isKey;

/**
 * Casts `value` to a path array if it's not one.
 *
 * @private
 * @param {*} value The value to inspect.
 * @param {Object} [object] The object to query keys on.
 * @returns {Array} Returns the cast property path array.
 */
function castPath(value, object) {
  if (isArray_1(value)) {
    return value;
  }
  return _isKey(value, object) ? [value] : _stringToPath(toString_1(value));
}
var _castPath = castPath;

/** Used as references for various `Number` constants. */
var INFINITY = 1 / 0;

/**
 * Converts `value` to a string key if it's not a string or symbol.
 *
 * @private
 * @param {*} value The value to inspect.
 * @returns {string|symbol} Returns the key.
 */
function toKey(value) {
  if (typeof value == 'string' || isSymbol_1(value)) {
    return value;
  }
  var result = value + '';
  return result == '0' && 1 / value == -INFINITY ? '-0' : result;
}
var _toKey = toKey;

/**
 * The base implementation of `_.get` without support for default values.
 *
 * @private
 * @param {Object} object The object to query.
 * @param {Array|string} path The path of the property to get.
 * @returns {*} Returns the resolved value.
 */
function baseGet(object, path) {
  path = _castPath(path, object);
  var index = 0,
    length = path.length;
  while (object != null && index < length) {
    object = object[_toKey(path[index++])];
  }
  return index && index == length ? object : undefined;
}
var _baseGet = baseGet;

/**
 * Gets the value at `path` of `object`. If the resolved value is
 * `undefined`, the `defaultValue` is returned in its place.
 *
 * @static
 * @memberOf _
 * @since 3.7.0
 * @category Object
 * @param {Object} object The object to query.
 * @param {Array|string} path The path of the property to get.
 * @param {*} [defaultValue] The value returned for `undefined` resolved values.
 * @returns {*} Returns the resolved value.
 * @example
 *
 * var object = { 'a': [{ 'b': { 'c': 3 } }] };
 *
 * _.get(object, 'a[0].b.c');
 * // => 3
 *
 * _.get(object, ['a', '0', 'b', 'c']);
 * // => 3
 *
 * _.get(object, 'a.b.c', 'default');
 * // => 'default'
 */
function get(object, path, defaultValue) {
  var result = object == null ? undefined : _baseGet(object, path);
  return result === undefined ? defaultValue : result;
}
var get_1 = get;

function applyFilter(filter, item, searchMeta) {
  const fieldValue = get_1(item, filter.fieldID);
  let result;
  switch (filter.type) {
    case 'number':
      result = filterNumeric(filter, fieldValue);
      break;
    case 'date':
      result = filterDate(filter, fieldValue);
      break;
    case 'text':
    case 'list':
    default:
      result = filterAlphabetic(filter, fieldValue);
      break;
  }
  if (searchMeta && result) {
    searchMeta.set(item.id, filter.fieldID);
  }
  return result;
}
function applyFilters({ criteria, operator }, searchMeta) {
  return (item) => {
    const fn = filter => applyFilter(filter, item, searchMeta);
    return operator === 'or' ? criteria.some(fn) : criteria.every(fn);
  };
}
function applySearch({ fields, method, value }, searchMeta) {
  const searchConditions = fields.map(fieldID => ({
    fieldID,
    method,
    value,
    type: 'text',
  }));
  return applyFilters({
    operator: 'or',
    criteria: searchConditions,
  }, searchMeta);
}
function filter(data, { filters, search } = {}, searchMeta) {
  let result = data;
  if (filters) {
    result = result.filter(applyFilters(filters));
  }
  if (search) {
    result = result.filter(applySearch(search, searchMeta));
  }
  return result;
}

const comparators = {
  number: compareNumeric,
  date: compareDate,
  text: compareAlphabetic,
};
function sort(data, { sorts } = {}) {
  if (!sorts) {
    return data;
  }
  return sorts.reduce((seq, sort, i) => {
    const keySelector = item => get_1(item, sort.fieldID);
    const dir = sort.direction === 'dsc' ? -1 : 1;
    const comparator = (a, b) => comparators[sort.type || 'text'](dir, a, b);
    return seq[(i === 0) ? 'sortBy' : 'thenBy'](keySelector, comparator);
  }, data);
}

const aggregators = {
  avg: items => from(items).sum() / items.length,
  count: items => items.length,
  distinct: items => from(items).distinct().toArray().length,
  empty: items => items.filter(i => i == null).length,
  filled: items => items.filter(i => i != null).length,
  max: items => items.reduce((s, i) => Math.max(s, i), -Infinity),
  min: items => items.reduce((s, i) => Math.min(s, i), Infinity),
  sum: items => from(items).sum(),
};
function group(data, { group } = {}) {
  const field = group.field;
  const aggr = group.aggregation;
  const aggregator = typeof aggr === 'function' ? aggr : aggregators[aggr];
  return data.groupBy(field)
    .sortBy(g => g.key)
    .map(g => {
    return {
      field: ('' + g.key),
      items: g.items,
      aggregation: aggr && (aggregator === null || aggregator === void 0 ? void 0 : aggregator(g.items))
    };
  })
    .toArray();
}

function applyState(data, state) {
  const result = {};
  let sequence = from(data);
  const search = new Map();
  for (const fn of [filter, sort]) {
    sequence = fn(sequence, state, search);
  }
  if (state.search) {
    result.search = search;
  }
  if (state.group) {
    result.groups = group(sequence, state);
  }
  if (state.pagination) {
    sequence = sequence
      .skip(state.pagination.currentPage)
      .take(state.pagination.itemsPerPage);
  }
  result.data = sequence.toArray();
  return result;
}

export { applyState as a, filterNumeric as b, compareAlphabetic as c, compareNumeric as d, filterDate as e, filterAlphabetic as f, compareDate as g, from as h, get_1 as i };
